/**
 * useFeedbackTraceDetail — React Query hook for lazy Langfuse
 * input/output fetch (Feature 2 — Feedback trace detail).
 *
 * Wire shape:
 *   GET /salescoach/api/feedback/trace-detail?traceId=<id>
 *     200 → { input, output, error }
 *     400 / 502 → throws ApiError
 *
 * The hook is `enabled` only when BOTH `traceId` is non-null AND the card
 * passes `enabled=true` (i.e., the expanded state is on). This matches
 * the rest of the dashboard's pattern of tying network traffic to DOM
 * visibility. `staleTime: 5 min` mirrors the server-side LRU inside
 * `fetchLangfuseTrace` — Langfuse traces are immutable post-completion,
 * so a re-expand never triggers a second round trip.
 *
 * The response shape is defined locally here (not in api-types.ts) because
 * the spec reserves contract additions to the backend agent. If the type
 * ends up being widely re-used, promote it to api-types later.
 */

import { useQuery } from '@tanstack/react-query';

import type { CodeDrawnBlock } from '@/components/trace/TraceDetailPanel';
import { apiFetch } from '@/services/api';

const STALE_TIME_MS = 5 * 60 * 1000;

export interface FeedbackTraceDetailResponse {
  input: string | null;
  output: string | null;
  error: string | null;
  /** What the CODE drew beside the answer on that turn — the connect card today.
   *  Absent on the great majority of turns, which drew nothing. */
  codeDrawn?: CodeDrawnBlock[];
}

export function useFeedbackTraceDetail(
  traceId: string | null,
  enabled: boolean,
) {
  return useQuery<FeedbackTraceDetailResponse>({
    queryKey: ['feedback', 'trace-detail', traceId],
    queryFn: async () =>
      apiFetch<FeedbackTraceDetailResponse>(
        `/salescoach/api/feedback/trace-detail?traceId=${encodeURIComponent(
          traceId as string,
        )}`,
      ),
    enabled: enabled && Boolean(traceId),
    staleTime: STALE_TIME_MS,
    // One retry — the endpoint returns 502 on transient upstream issues,
    // and the manual Retry button below the skeleton covers the hard case.
    retry: 1,
  });
}
